/* Explicit, consent-gated storefront actions. Shopify owns page/commerce events. */
(() => {
  if (window.cpJourneyAnalyticsLoaded) return;
  const node = document.getElementById('cp-journey-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch { return; }
  const qa = config.mode === 'qa';
  const debug = qa || new URLSearchParams(location.search).get('cp_analytics_qa') === '1';
  if (!config.enabled || window.Shopify?.designMode) return;
  if (/^\/(account|checkout|orders|authentication|challenge)(\/|$)/.test(location.pathname)) return;
  window.cpJourneyAnalyticsLoaded = true;
  const clean = value => String(value || '').replace(/[^a-zA-Z0-9 ._:/-]/g, '').slice(0,100);
  const destination = href => { try { const u = new URL(href,location.origin); return u.origin === location.origin ? u.pathname : ''; } catch { return ''; } };
  const permitted = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  node.dataset.captureState='waiting';
  let active = false, controller, observer, mutations, pulse, documentId, seq, total, lastTick, lastInput, maxScroll;
  let sections, timers, seenCards, scrolls, attempts, previousVariants, lastSummary, landingImpressions, lastAddAt, calendarSignals, confirmedAdds, mapEngaged, mapSignals, mapSeen;
  function emit(name, properties = {}, extra = {}) {
    if (!active || !permitted()) return;
    try {
      if(debug)console.debug('[CP analytics QA]',name,properties);
      const result = window.Shopify.analytics.publish(name, {
        signal_id: documentId + '_' + (++seq), document_id: documentId,
        release_id: clean(config.release_id) || 'unknown', page_type: config.page_type,
        product_id: config.product_id, variant_id: config.variant_id, qa,
        properties, ...extra,
      });
      if (result?.catch) result.catch(() => {});
    } catch { /* Tracking must not interrupt the storefront. */ }
  }
  function measure() {
    const now = performance.now();
    if (!document.hidden && now-lastInput < 60000) total += Math.min(1000,Math.max(0,now-lastTick));
    lastTick = now;
    const available = document.documentElement.scrollHeight-innerHeight;
    const depth = available > 16 ? Math.min(100,Math.max(0,scrollY/available*100)) : 0;
    maxScroll = Math.max(maxScroll,depth);
    for (const threshold of [25,50,75,90]) if (available>16 && depth>=threshold && !scrolls.has(threshold)) {
      scrolls.add(threshold); emit('cp:scroll_depth',{threshold,scroll_applicable:true});
    }
  }
  function summary() {
    if (!active || !permitted()) return;
    measure();
    const properties={active_ms:Math.round(total),max_scroll:maxScroll,scroll_applicable:document.documentElement.scrollHeight-innerHeight>16};
    const signature=JSON.stringify(properties);
    if(signature===lastSummary)return;
    lastSummary=signature;
    emit('cp:page_summary',properties);
  }
  function context() {
    emit('cp:page_context',{page_type:config.page_type,product_kind:config.product_kind,eligible_sections:[...new Set([...sections].filter(el=>el.isConnected).map(el=>el.dataset.cpAnalyticsSection))]});
  }
  function observe() {
    let changed = false;
    for(const [selector,key] of [['#shop-by-place','shop-by-place'],['.cp-review-widget','reviews'],['.cp-gifts-feature','gifts'],['.cp-business-services','business-services'],['media-gallery','product-gallery']]){for(const el of document.querySelectorAll(selector))el.dataset.cpAnalyticsSection=key;}
    for (const el of document.querySelectorAll('[data-cp-analytics-section]')) if (!sections.has(el)) { sections.add(el); observer.observe(el); changed=true; }
    for (const el of document.querySelectorAll('product-card[data-product-id]')) if (!seenCards.has(el)) { seenCards.add(el); observer.observe(el); }
    if (changed) context();
  }
  function cardInfo(card) {
    const list = card.closest('[data-list-analytics-event],.shopify-section');
    return {list_id:clean(list?.dataset.listAnalyticsEvent || list?.id || 'products'),position:[...(list||document).querySelectorAll('product-card')].indexOf(card)+1};
  }
  function start() {
    if(active)return;
    if(!permitted()){node.dataset.captureState='consent-required';return;}
    if(!window.Shopify?.analytics?.publish){node.dataset.captureState='pixel-api-unavailable';return;}
    if(qa){try{if(new URLSearchParams(location.search).get('cp_analytics_qa')==='1')sessionStorage.setItem('cp_analytics_qa','1');if(sessionStorage.getItem('cp_analytics_qa')!=='1')return;}catch{return;}}
    active=true;node.dataset.captureState='active';controller=new AbortController(); documentId=crypto.randomUUID();seq=0;total=0;maxScroll=0;lastSummary=null;
    lastTick=lastInput=performance.now();sections=new Set();timers=new Map();seenCards=new WeakSet();scrolls=new Set();attempts=new Map();previousVariants=new Map();landingImpressions=new Set();lastAddAt=0;calendarSignals=new Set();confirmedAdds=new WeakSet();mapEngaged=false;mapSignals=new Map();mapSeen=false;
    const listen=(target,name,callback,options={})=>target.addEventListener(name,callback,{...options,signal:controller.signal});
    observer=new IntersectionObserver(entries=>{
      for (const entry of entries) {
        clearTimeout(timers.get(entry.target));timers.delete(entry.target);
        if (!entry.isIntersecting || document.hidden) continue;
        const el=entry.target;
        // A section's top marker must enter the viewport; cards need at least half visible.
        if (el.matches('product-card') && entry.intersectionRatio<0.5) continue;
        const isMap = el.dataset.cpAnalyticsSection === 'location-map';
        if (isMap && mapSeen) continue;
        if (isMap ? entry.intersectionRatio < 0.25 : !el.matches('product-card') && entry.boundingClientRect.top<0) continue;
        timers.set(el,setTimeout(()=>{
          if (!active || document.hidden) return;
          if (el.matches('product-card')) {
            const info=cardInfo(el);
            const landingKey=info.list_id.startsWith('landing:')?info.list_id+':'+el.dataset.productId:null;
            if(!landingKey||!landingImpressions.has(landingKey)){
              emit('cp:list_items_seen',{list_id:info.list_id},{items:[{product_id:el.dataset.productId,position:info.position,impression_id:documentId+'_'+seq}]});
              if(landingKey)landingImpressions.add(landingKey);
            }
          } else {
            emit('cp:section_seen',{section:el.dataset.cpAnalyticsSection,visibility_rule:isMap?'quarter_visible_1s':'top_visible_1s'});
            if (isMap) mapSeen = true;
          }
          observer.unobserve(el);timers.delete(el);
        },1000));
      }
    },{threshold:[0,0.25,0.5,1]});
    observe();context();
    let mutationTimer;
    mutations=new MutationObserver(()=>{clearTimeout(mutationTimer);mutationTimer=setTimeout(()=>{if(active)observe();},250);});
    mutations.observe(document.querySelector('main')||document.body,{childList:true,subtree:true});
    for (const name of ['pointerdown','keydown','scroll']) listen(window,name,()=>{lastInput=performance.now();},{passive:true});
    listen(document,'visibilitychange',()=>{summary();for(const t of timers.values())clearTimeout(t);timers.clear();if(!document.hidden){lastTick=performance.now();for(const el of sections){observer.unobserve(el);observer.observe(el);}}});
    listen(window,'pagehide',summary);
    listen(window,'pageshow',event=>{if(event.persisted){stop();start();}});
    // Capture short visits before navigation can discard the final pixel delivery.
    // Values remain cumulative; unchanged idle/hidden pages add no repeated summaries.
    let ticks=0;pulse=setInterval(()=>{measure();if(!document.hidden&&(++ticks===1||ticks%5===0))summary();},1000);
    const engageMap = () => {
      if (!mapEngaged) { mapEngaged = true; emit('cp:gallery_interacted',{action:'map_engaged'}); }
    };
    listen(document,'cp:map-interaction',event=>{
      // Compare the tag name so a host with a shadowed Element.matches() still counts.
      if (!(event.target instanceof Element) || event.target.localName !== 'cp-location-map') return;
      const {control,value}=event.detail||{};
      const selections=['place_pin','place_pill','place_list','place_nearby','place_search','search_suggestion','area','back_to_coast'];
      const actions=['pan','zoom','cluster','reset','search_clear','view_photos','back_to_map','discover','gallery_more','gallery_retry','gallery_browse'];
      let name,properties;
      if(control==='search' && Number.isInteger(value) && value>=0 && value<=10000) {
        name='cp:discovery_changed';properties={control:'map_search',value:'results:'+value};
      } else if(selections.includes(control) && typeof value==='string' && /^[a-z0-9-]{1,80}$/.test(value)) {
        name='cp:discovery_changed';properties={control:'map_'+control,value};
      } else if(actions.includes(control)) {
        name='cp:gallery_interacted';properties={action:'map_'+control};
        if(control==='gallery_browse') {
          if(!Number.isInteger(value)||value<1||value>10000)return;
          properties.media_index=value;
        }
      } else return;
      // Bound noisy gestures without counting automatic camera fits or page restoration.
      const key=control+':'+String(value),now=performance.now();
      if(mapSignals.has(key)&&now-mapSignals.get(key)<1000)return;
      if(mapSignals.size>=100)mapSignals.clear();
      mapSignals.set(key,now);engageMap();emit(name,properties);
    });
    listen(document,'click',event=>{
      const target=event.target instanceof Element?event.target:null;if(!target)return;
      const mapLink=target.closest('cp-location-map a[data-homepage-event]');
      if(mapLink)engageMap();
      const link=target.closest('a[href]'),card=target.closest('product-card'),matching=target.closest('[data-cp-matching-product]');
      if (link && matching) emit('cp:matching_product_clicked',{destination:destination(link.href),destination_product_id:matching.dataset.cpMatchingProduct,destination_kind:matching.dataset.cpMatchingKind});
      else if (link && card) emit('cp:product_card_clicked',{...cardInfo(card),destination:destination(link.href)},{product_id:card.dataset.productId});
      else if (link) emit('cp:navigation_clicked',{destination:destination(link.href),placement:mapLink?'location-map':link.closest('header')?'header':link.closest('footer')?'footer':'content',link_key:clean(link.closest('[data-homepage-event]')?.dataset.homepageEvent||link.getAttribute('href')?.split('?')[0])});
      const preview=target.closest('[data-format-preview]');if(preview&&!preview.disabled)emit('cp:print_preview_changed',{mode:'format',format:clean(preview.dataset.formatPreview)});
      if(target.closest('media-gallery')&&target.closest('button'))emit('cp:gallery_interacted',{action:'control_clicked'});
      if(target.closest('[name="checkout"],a[href^="/checkout"]'))emit('cp:checkout_clicked',{placement:target.closest('dialog,cart-drawer-component')?'drawer':'cart'});
      // The Inbox chat launcher lives in a shadow root: count taps on its exported activator part only.
      if(target.localName==='shopify-chat'&&(event.composedPath?.()||[]).some(node=>node.getAttribute?.('part')?.split(' ').includes('activator')))emit('cp:information_opened',{section:'chat_launcher'});
      const summaryEl=target.closest('summary');if(summaryEl&&!summaryEl.parentElement.open)emit('cp:information_opened',{section:clean(summaryEl.closest('[data-cp-analytics-section]')?.dataset.cpAnalyticsSection||summaryEl.id||'product_information')});
    });
    listen(document,'variant:update',event=>{
      const variant=event.detail?.resource,productId=String(event.detail?.data?.productId||'');if(!variant?.id)return;
      const old=previousVariants.get(productId)||config.variant_id;
      if(String(variant.id)===String(old))return;previousVariants.set(productId,String(variant.id));
      const picker=[...document.querySelectorAll('variant-picker')].find(el=>el.dataset.productId===productId);
      const properties={previous_variant_id:String(old||''),change_source:'picker'};
      for(const option of ['size','format','border','paper']) {const group=picker?.querySelector('[data-option-name="'+option+'"]');properties[option]=clean(group?.querySelector('select')?.value||group?.querySelector('input:checked')?.value);}
      emit('cp:variant_selected',properties,{product_id:productId,variant_id:String(variant.id)});
    });
    listen(document,'submit',event=>{
      const form=event.target;if(!(form instanceof HTMLFormElement)||!form.closest('product-form-component'))return;
      const component=form.closest('product-form-component'),data=new FormData(form),attemptId=crypto.randomUUID();
      const placement=form.closest('dialog,quick-add-component,product-card')?'quick_add':'product';
      attempts.set(form.id,{id:attemptId,at:Date.now(),placement,productId:String(component.dataset.productId||''),variantId:String(data.get('id')||'')});
      emit('cp:add_to_cart_attempted',{attempt_id:attemptId,quantity:Number(data.get('quantity'))||1,placement},{product_id:component.dataset.productId,variant_id:String(data.get('id')||'')});
    },{capture:true});
    // A confirmed add: Horizon's product form dispatches cart:update with source 'product-form-component'.
    listen(document,'cart:update',event=>{
      const data=event.detail?.data||{};
      if(data.didError||(data.source!=='product-form-component'&&data.source!=='cp-gift-pack-builder'))return;
      if(confirmedAdds.has(event))return;confirmedAdds.add(event);
      const productId=String(data.productId||'');let matched=null;
      for(const [formId,attempt] of attempts){if(attempt.productId===productId&&Date.now()-attempt.at<30000){matched=attempt;attempts.delete(formId);break;}}
      lastAddAt=Date.now();
      emit('cp:add_to_cart_succeeded',{attempt_id:matched?.id||'',placement:matched?.placement||clean(data.placement)||'unknown',quantity:Number(data.addedQuantity??data.itemCount)||1},{product_id:productId,variant_id:String(event.detail?.sourceId||matched?.variantId||'')});
    });
    listen(document,'calendar:cart',event=>{
      const data=event.detail||{};
      if(!['attempted','succeeded','failed'].includes(data.phase)||!data.attemptId)return;
      const key=data.attemptId+':'+data.phase;
      if(calendarSignals.has(key))return;calendarSignals.add(key);
      const quantity=Number.isInteger(data.quantity)&&data.quantity>0?{quantity:data.quantity}:{};
      const extra={product_id:String(data.productId||''),variant_id:String(data.variantId||'')};
      if(data.operation==='complete') {
        emit('cp:cart_interacted',{action:'calendar_completed',outcome:data.phase==='attempted'?'requested':data.phase==='succeeded'?'confirmed':'unconfirmed',...quantity},extra);
      } else if(data.operation==='add') {
        if(data.phase==='failed')emit('cp:add_to_cart_failed',{attempt_id:data.attemptId,failure:'unconfirmed'},extra);
        else emit('cp:add_to_cart_'+data.phase,{attempt_id:data.attemptId,placement:'calendar_builder',...quantity},extra);
      }
    });
    // The drawer's open event does not bubble, so listen in the capture phase.
    listen(document,'dialog:open',event=>{
      const target=event.target;
      if(!(target instanceof Element)||!target.matches?.('cart-drawer-component'))return;
      emit('cp:drawer_opened',{trigger:Date.now()-lastAddAt<3000?'add_to_cart':'manual'});
    },{capture:true});
    listen(document,'cart:error',event=>{const attempt=attempts.get(event.detail?.sourceId);if(attempt&&Date.now()-attempt.at<30000){emit('cp:add_to_cart_failed',{attempt_id:attempt.id,failure:'cart_rejected'});attempts.delete(event.detail.sourceId);}});
    // Horizon emits this event for both quantity buttons and typed quantities.
    listen(document,'quantity-selector:update',event=>{
      const {quantity,cartLine}=event.detail||{};
      if(!Number.isInteger(cartLine)||cartLine<1||!Number.isFinite(quantity)||quantity<0)return;
      emit('cp:cart_interacted',{action:'quantity_changed',quantity,outcome:'requested'});
    });
    listen(document,'change',event=>{const target=event.target;if(!(target instanceof HTMLInputElement||target instanceof HTMLSelectElement))return;
      if(target.name==='sort_by')emit('cp:discovery_changed',{control:'sort',value:clean(target.value)});
      if(target.name?.startsWith('filter.'))emit('cp:discovery_changed',{control:clean(target.name),value:target.type==='checkbox'?(target.checked?'selected':'cleared'):'changed'});
    });
    if(config.page_type==='search')emit('cp:search_results',{search_id:documentId,result_count:config.search_result_count,surface:'search_page'});
  }
  function stop() {
    active=false;node.dataset.captureState='paused';controller?.abort();observer?.disconnect();mutations?.disconnect();clearInterval(pulse);
    for(const timer of timers?.values()||[])clearTimeout(timer);attempts?.clear();documentId=null;
  }
  document.addEventListener('visitorConsentCollected',()=>{if(permitted())start();else{stop();try{sessionStorage.removeItem('cp_analytics_qa');}catch{}}});
  if(window.Shopify?.customerPrivacy)start();
  else window.Shopify?.loadFeatures?.([{name:'consent-tracking-api',version:'0.1'}],error=>{if(!error)start();});
})();
